import "./PaymentTerms.scss";
import React from "react";
import {observer} from "mobx-react-lite";
import {Box, Button, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import {
    AccountBalanceOutlined,
    CallSplitOutlined,
    FileDownloadOutlined,
    StackedBarChartOutlined,
    TimelineOutlined,
} from "@mui/icons-material";
import {useGridApiRef} from "@mui/x-data-grid-premium";
import {MainPageFacade} from "../../components/main/page/MainPageFacade.tsx";
import {CARD_CONFIG} from "../../components/dashboard/DashboardCardConfigs.tsx";
import {KpiBoxedValues} from "../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import type {KpiBoxedValue} from "../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import type {PtSupplier} from "./paymentTermsDemoData";
import {PT_KPIS, PT_LONG_TAIL, PT_PERIOD, PT_POLICY} from "./paymentTermsDemoData";
import type {PtExclusions} from "./paymentTermsUtils";
import {formatCompactCurrency, formatPct, loadExclusions, saveExclusions} from "./paymentTermsUtils";
import {exportPaymentTermsCsv} from "./paymentTermsExport";
import {PaymentTermsTable} from "./components/PaymentTermsTable";
import {SupplierTermsDrawer} from "./components/SupplierTermsDrawer";
import {TermLandscapeView} from "./components/TermLandscapeView";
import {HarmonisationView} from "./components/HarmonisationView";
import {TrendView} from "./components/TrendView";
import {usePaymentTermsSuppliersQuery} from "./usePaymentTermsQueries";

type PtMode = 'opportunities' | 'landscape' | 'harmonisation' | 'trend';

const MODES: {value: PtMode; label: string; icon: React.ReactNode}[] = [
    {value: 'opportunities', label: 'Opportunities', icon: <AccountBalanceOutlined fontSize="small"/>},
    {value: 'landscape', label: 'Term landscape', icon: <StackedBarChartOutlined fontSize="small"/>},
    {value: 'harmonisation', label: 'Harmonisation', icon: <CallSplitOutlined fontSize="small"/>},
    {value: 'trend', label: 'Trend', icon: <TimelineOutlined fontSize="small"/>},
];

export const PaymentTermsPage: React.FC = observer(() => {
    const apiRef = useGridApiRef();
    const [mode, setMode] = React.useState<PtMode>('opportunities');
    const [selected, setSelected] = React.useState<PtSupplier | null>(null);
    const [exclusions, setExclusions] = React.useState<PtExclusions>(loadExclusions);
    const {data: suppliers = [], isPending: suppliersLoading} = usePaymentTermsSuppliersQuery();

    const k = PT_KPIS;

    const updateExclusions = (next: PtExclusions) => {
        setExclusions(next);
        saveExclusions(next);
    };

    // The review workflow removes disqualified suppliers from the opportunity totals.
    const excluded = React.useMemo(() =>
        suppliers.filter(s => exclusions[s.id]), [suppliers, exclusions]);
    const excludedWcMax = excluded.reduce((sum, s) => sum + s.wcOpportunity, 0);
    const excludedWcMin = excluded.reduce((sum, s) => sum + s.quickWinOpportunity, 0);
    const wcMax = k.wcOpportunity - excludedWcMax;
    const wcMin = k.quickWinOpportunity - excludedWcMin;

    const kpisTop: KpiBoxedValue[] = [
        {
            key: 'spend',
            title: 'Total spend (12m)',
            subtitle: formatCompactCurrency(k.totalPositiveSpend, k.currency),
            help: `Total positive third-party spend in scope, ${PT_PERIOD.from} – ${PT_PERIOD.to}. ` +
                `${formatPct(k.termCoveragePct)} of it has mapped payment terms.`,
        },
        {
            key: 'wc',
            title: 'Working capital opportunity',
            subtitle: `${formatCompactCurrency(wcMin, k.currency)} – ${formatCompactCurrency(wcMax, k.currency)}`,
            help: 'Range of one-time cash release. Minimum: align every entity to the best term each supplier ' +
                `already accepts (quick win). Maximum: extend every below-target supplier to the ${PT_POLICY.targetTermDays}-day ` +
                'policy term (spend × term gap ÷ 365).' +
                (excluded.length > 0 ? ` Adjusted for ${excluded.length} supplier(s) disqualified in review.` : ''),
        },
        {
            key: 'term',
            title: 'Weighted avg payment term',
            subtitle: `${k.weightedAvgTerm.toFixed(1)} days`,
            help: `Spend-weighted average of contractual payment terms across all suppliers. Policy target is ${PT_POLICY.targetTermDays} days. ` +
                'See the Trend view for month-by-month progress.',
        },
    ];
    const kpisBottom: KpiBoxedValue[] = [
        {
            key: 'below',
            title: 'Spend below target terms',
            subtitle: <>{formatCompactCurrency(k.spendBelowTarget, k.currency)}
                <span className="pt-kpi-sub"> · {formatPct(k.spendBelowTargetPct)}</span></>,
            help: `Spend where the current payment term is shorter than the ${PT_POLICY.targetTermDays}-day target — ` +
                'the base the working capital opportunity is computed on.',
        },
        {
            key: 'immediate',
            title: 'Immediate payment exposure',
            subtitle: <>{formatCompactCurrency(k.immediateExposure, k.currency)}
                <span className="pt-kpi-sub"> · {formatPct(k.immediateExposurePct)}</span></>,
            help: 'Spend paid on immediate (0-day) terms — the fastest lever to release cash.',
        },
        {
            key: 'policy',
            title: 'Suppliers meeting policy',
            subtitle: formatPct(k.suppliersMeetingPolicyPct),
            help: `Share of suppliers whose weighted term meets the ${PT_POLICY.targetTermDays}-day policy.`,
        },
    ];

    const modeSelector = (
        <Box className="pt-modes" display="flex" alignItems="center" gap={1.5}>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(_e, v) => v && setMode(v)}>
                {MODES.map(m => (
                    <ToggleButton key={m.value} value={m.value}>
                        {m.icon}{m.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadOutlined/>}
                onClick={() => exportPaymentTermsCsv(suppliers, exclusions)}>
                Export
            </Button>
        </Box>
    );

    const scopeStrip = (
        <Box className="pt-scope-strip">
            <ScopeItem label="Analysis period" value={`${PT_PERIOD.from} – ${PT_PERIOD.to}`} hint={PT_PERIOD.label}/>
            <ScopeItem label="Parent suppliers" value={k.supplierCount.toLocaleString('en-US')}/>
            <ScopeItem label="Transactions" value={k.transactionCount.toLocaleString('en-US')}/>
            <ScopeItem label="Term coverage" value={formatPct(k.termCoveragePct)}/>
            <ScopeItem label="Target policy" value={`${PT_POLICY.targetTermDays} days`}/>
        </Box>
    );

    return <MainPageFacade
        title={CARD_CONFIG['payment-terms'].title}
        className="payment-terms-page"
        actionBarEnd={modeSelector}>

        {scopeStrip}

        {mode !== 'harmonisation' && <>
            <KpiBoxedValues values={kpisTop}/>
            <KpiBoxedValues values={kpisBottom}/>
        </>}

        {mode === 'opportunities' &&
            <Box className="pt-table-card">
                <Box className="pt-table-card-header">
                    <Typography variant="h2" sx={{fontSize: 18, fontWeight: 700}}>
                        Supplier opportunities ({suppliers.length - excluded.length})
                        {excluded.length > 0 &&
                            <span className="pt-excluded-count"> · {excluded.length} excluded in review</span>}
                    </Typography>
                    <span className="pt-table-card-hint">
                        {PT_LONG_TAIL.count.toLocaleString('en-US')} long-tail suppliers
                        ({formatCompactCurrency(PT_LONG_TAIL.wcOpportunity, k.currency)}) hidden ·
                        Click a row to review, drill in or disqualify.
                    </span>
                </Box>
                <PaymentTermsTable
                    rows={suppliers}
                    apiRef={apiRef}
                    exclusions={exclusions}
                    loading={suppliersLoading}
                    onRowClick={setSelected}
                />
            </Box>
        }

        {mode === 'landscape' && <TermLandscapeView/>}
        {mode === 'harmonisation' && <HarmonisationView rows={suppliers} onSelect={setSelected}/>}
        {mode === 'trend' && <TrendView/>}

        <Typography className="pt-scope-note" variant="caption">
            {PT_POLICY.scopeNote}
        </Typography>

        <SupplierTermsDrawer
            supplier={selected}
            exclusionReason={selected ? exclusions[selected.id] : undefined}
            onExclude={(s, reason) => updateExclusions({...exclusions, [s.id]: reason})}
            onRequalify={s => {
                const next = {...exclusions};
                delete next[s.id];
                updateExclusions(next);
            }}
            onClose={() => setSelected(null)}
        />
    </MainPageFacade>;
});

const ScopeItem: React.FC<{label: string; value: string; hint?: string}> = ({label, value, hint}) => (
    <Box className="pt-scope-item">
        <span className="pt-scope-item-label">{label}</span>
        <span className="pt-scope-item-value">{value}</span>
        {hint && <span className="pt-scope-item-hint">{hint}</span>}
    </Box>
);
